import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShoppingCart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { StoreSEO } from "@/components/seo/StoreSEO";
import { ProductJsonLd } from "@/components/seo/ProductJsonLd";
import { getProductCanonicalUrl } from "@/lib/seo-utils";
import { useMemo } from "react";
import { formatStorePrice, useStoreCurrency } from "@/lib/storeCurrency";
import { useStoreLanguages } from "@/hooks/useStoreLanguages";
import { useStoreContentTranslations, type ContentSource } from "@/hooks/useStoreContentTranslations";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      if (!id) throw new Error("Product ID is required");

      // Try finding by slug first, then by UUID
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

      const query = supabase
        .from('products')
        .select('*')
        .single();

      const { data, error } = isUuid
        ? await supabase.from('products').select('*').eq('id', id).single()
        : await supabase.from('products').select('*').eq('slug', id).single();

      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  const storeId = (product as any)?.store_id ?? null;
  const langs = useStoreLanguages(storeId);
  const currency = useStoreCurrency(storeId);
  const contentSources = useMemo<ContentSource[]>(() => product ? [{
    table: "products", id: product.id,
    fields: { name: product.name, description: product.description, category: product.category, seo_title: product.seo_title, seo_description: product.seo_description },
  }] : [], [product]);
  const { tr: trRaw } = useStoreContentTranslations(storeId, langs.currentLanguage, langs.defaultLanguage, langs.enabledLanguages, contentSources);
  const tr = (field: string, original: string | null | undefined) => (product ? trRaw("products", product.id, field, original) : original ?? "");

  const handleAddToCart = () => {
    toast({
      title: "Ajouté au panier",
      description: <span><span translate="no">{tr("name", product?.name)}</span> a été ajouté à votre panier</span>,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="container mx-auto max-w-4xl text-center py-16">
          <h1 className="text-2xl font-bold mb-4">Produit non trouvé</h1>
          <p className="text-muted-foreground mb-6">Ce produit n'existe pas ou a été supprimé.</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  const imageUrl = product.image || 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&h=600&fit=crop&q=80';
  const canonicalUrl = product.slug ? getProductCanonicalUrl(product.slug) : undefined;

  return (
    <div className="min-h-screen bg-background">
      <StoreSEO
        title={product.seo_title ? tr("seo_title", product.seo_title) : tr("name", product.name)}
        description={product.seo_description ? tr("seo_description", product.seo_description) : product.description ? tr("description", product.description) : `Achetez ${tr("name", product.name)}`}
        ogImage={imageUrl}
        canonicalUrl={canonicalUrl}
        productName={product.name}
        productPrice={product.price}
        productImage={imageUrl}
        productAvailability={(product.available_stock ?? product.stock ?? 0) > 0}
        type="product"
      />
      <ProductJsonLd
        name={product.name}
        description={product.description || undefined}
        image={imageUrl}
        price={product.price}
        availability={(product.available_stock ?? product.stock ?? 0) > 0}
        url={canonicalUrl || window.location.href}
      />

      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-lg overflow-hidden bg-muted">
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-96 object-cover"
              loading="lazy"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&h=600&fit=crop&q=80';
              }}
            />
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground" translate="no">{tr("name", product.name)}</h1>
              {product.category && (
                <span className="text-sm text-muted-foreground" translate="no">{tr("category", product.category)}</span>
              )}
            </div>

            <div className="text-3xl font-bold text-primary">
              {formatStorePrice(product.price, currency)}
            </div>

            {product.description && (
              <div className="prose prose-sm max-w-none">
                <p className="text-muted-foreground" translate="no">{tr("description", product.description)}</p>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Stock:</span>
              <span className={(product.available_stock ?? product.stock ?? 0) > 0 ? "text-green-600" : "text-red-600"}>
                {(product.available_stock ?? product.stock ?? 0) > 0 ? `${product.available_stock ?? product.stock} disponible(s)` : "Rupture de stock"}
              </span>
            </div>

            <Button
              size="lg"
              className="w-full"
              onClick={handleAddToCart}
              disabled={(product.available_stock ?? product.stock ?? 0) <= 0}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Ajouter au panier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
